import { useEffect, useId, useRef, useState, type ButtonHTMLAttributes, type KeyboardEvent, type ReactNode } from 'react';
import { describirEstado, estadoImporteDesde, ETIQUETA_ESTADO_IMPORTE, type EstadoImporte, type Tono } from '../estados';
import { formatoCLP } from '../formato';
import { LEYENDA_MINIMA } from '../leyenda';

type Variante = 'primario' | 'secundario' | 'peligro' | 'fantasma';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variante?: Variante;
  chico?: boolean;
  cargando?: boolean;
}

export function Button({ variante = 'secundario', chico, cargando, className, children, disabled, type, ...rest }: ButtonProps) {
  const clases = ['t-btn', variante !== 'secundario' ? `t-btn--${variante}` : null, chico ? 't-btn--chico' : null, className].filter(Boolean).join(' ');
  return (
    <button type={type ?? 'button'} className={clases} disabled={disabled || cargando} aria-busy={cargando || undefined} {...rest}>
      {cargando ? <span className="t-spinner" aria-hidden="true" /> : null}
      {children}
    </button>
  );
}

export function Badge({ tono = 'neutro', children }: { tono?: Tono; children: ReactNode }) {
  return <span className={`t-badge t-badge--${tono}`}>{children}</span>;
}

/** Insignia de estado con texto (nunca solo color). */
export function EstadoBadge({ estado }: { estado: string | null | undefined }) {
  const d = describirEstado(estado);
  return <Badge tono={d.tono}>{d.etiqueta}</Badge>;
}

/** Importe siempre acompañado de su estado (spec funcional §9.3). */
export function Importe({ monto, estado }: { monto: number | string | null | undefined; estado: EstadoImporte | string | null | undefined }) {
  const e: EstadoImporte =
    estado && (Object.keys(ETIQUETA_ESTADO_IMPORTE) as string[]).includes(estado) ? (estado as EstadoImporte) : estadoImporteDesde(estado ?? null);
  const d = describirEstado(e === 'enviado_nomina' ? 'enviado_a_nomina' : e);
  return (
    <span className="t-importe">
      <span className="t-importe__monto">{formatoCLP(monto)}</span>
      <Badge tono={d.tono}>
        <span className="t-sr-only">Estado: </span>
        {ETIQUETA_ESTADO_IMPORTE[e]}
      </Badge>
    </span>
  );
}

/** Leyenda obligatoria en toda vista con importes de trabajadores. */
export function Leyenda({ texto }: { texto?: string | null | undefined }) {
  return (
    <p className="t-leyenda" role="note" aria-label="Leyenda del comprobante">
      {texto && texto.trim() ? texto : LEYENDA_MINIMA}
    </p>
  );
}

export function Card({ titulo, acciones, children, as: Tag = 'section' }: { titulo?: ReactNode; acciones?: ReactNode; children: ReactNode; as?: 'section' | 'div' | 'article' }) {
  const id = useId();
  return (
    <Tag className="t-card" aria-labelledby={titulo ? id : undefined}>
      {titulo || acciones ? (
        <div className="t-card__titulo">
          {titulo ? <h2 id={id}>{titulo}</h2> : <span />}
          {acciones ? <div className="t-acciones">{acciones}</div> : null}
        </div>
      ) : null}
      {children}
    </Tag>
  );
}

export function Alert({ tono = 'info', titulo, children }: { tono?: 'info' | 'exito' | 'alerta' | 'peligro'; titulo?: ReactNode; children?: ReactNode }) {
  return (
    <div className={`t-alert t-alert--${tono}`} role={tono === 'peligro' ? 'alert' : 'status'}>
      {titulo ? <strong className="t-alert__titulo">{titulo}</strong> : null}
      {children}
    </div>
  );
}

export function PageHeader({ titulo, descripcion, acciones }: { titulo: ReactNode; descripcion?: ReactNode; acciones?: ReactNode }) {
  return (
    <header className="t-page-header">
      <div>
        <h1>{titulo}</h1>
        {descripcion ? <p>{descripcion}</p> : null}
      </div>
      {acciones ? <div className="t-acciones">{acciones}</div> : null}
    </header>
  );
}

export function Cargando({ texto = 'Cargando…' }: { texto?: string }) {
  return (
    <div className="t-cargando" role="status" aria-live="polite">
      <span className="t-spinner" aria-hidden="true" />
      {texto}
    </div>
  );
}

export function Vacio({ children }: { children: ReactNode }) {
  return <div className="t-vacio">{children}</div>;
}

export interface PasoItem {
  clave: string;
  etiqueta: string;
  estado: 'hecho' | 'actual' | 'pendiente';
}

export function Pasos({ pasos, etiqueta }: { pasos: PasoItem[]; etiqueta: string }) {
  return (
    <ol className="t-pasos" aria-label={etiqueta}>
      {pasos.map((p, i) => (
        <li key={p.clave} className={`t-paso t-paso--${p.estado}`} aria-current={p.estado === 'actual' ? 'step' : undefined}>
          <span className="t-paso__num">{p.estado === 'hecho' ? '✓' : i + 1}</span>
          {p.etiqueta}
          {p.estado === 'actual' ? <span className="t-sr-only"> (estado actual)</span> : null}
        </li>
      ))}
    </ol>
  );
}

export interface TabItem {
  clave: string;
  etiqueta: string;
}

export function Tabs({ tabs, activa, onCambio, etiqueta }: { tabs: TabItem[]; activa: string; onCambio: (clave: string) => void; etiqueta: string }) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const onKey = (e: KeyboardEvent, i: number) => {
    let j = i;
    if (e.key === 'ArrowRight') j = (i + 1) % tabs.length;
    else if (e.key === 'ArrowLeft') j = (i - 1 + tabs.length) % tabs.length;
    else return;
    e.preventDefault();
    const t = tabs[j];
    if (t) {
      onCambio(t.clave);
      refs.current[j]?.focus();
    }
  };
  return (
    <div className="t-tabs" role="tablist" aria-label={etiqueta}>
      {tabs.map((t, i) => (
        <button
          key={t.clave}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="button"
          role="tab"
          id={`tab-${t.clave}`}
          aria-selected={t.clave === activa}
          aria-controls={`panel-${t.clave}`}
          tabIndex={t.clave === activa ? 0 : -1}
          className="t-tab"
          onClick={() => onCambio(t.clave)}
          onKeyDown={(e) => onKey(e, i)}
        >
          {t.etiqueta}
        </button>
      ))}
    </div>
  );
}

export function TabPanel({ clave, children }: { clave: string; children: ReactNode }) {
  return (
    <div role="tabpanel" id={`panel-${clave}`} aria-labelledby={`tab-${clave}`}>
      {children}
    </div>
  );
}

export interface DialogProps {
  abierto: boolean;
  titulo: ReactNode;
  onCerrar: () => void;
  children: ReactNode;
  pie?: ReactNode;
  ancho?: boolean;
}

/** Diálogo modal sobre <dialog> nativo: foco atrapado y Escape por el navegador. */
export function Dialog({ abierto, titulo, onCerrar, children, pie, ancho }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const id = useId();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (abierto && !d.open) {
      if (typeof d.showModal === 'function') d.showModal();
      else d.setAttribute('open', '');
    } else if (!abierto && d.open) {
      if (typeof d.close === 'function') d.close();
      else d.removeAttribute('open');
    }
  }, [abierto]);
  if (!abierto) return null;
  return (
    <dialog
      ref={ref}
      className={`t-dialog${ancho ? ' t-dialog--ancho' : ''}`}
      aria-labelledby={id}
      onCancel={(e) => {
        e.preventDefault();
        onCerrar();
      }}
    >
      <div className="t-dialog__cabecera">
        <h2 id={id}>{titulo}</h2>
        <Button variante="fantasma" chico onClick={onCerrar} aria-label="Cerrar">
          ✕
        </Button>
      </div>
      <div className="t-dialog__cuerpo">{children}</div>
      {pie ? <div className="t-dialog__pie">{pie}</div> : null}
    </dialog>
  );
}

/** Hook mínimo para estados de diálogos con dato asociado. */
export function useDialogo<T = true>() {
  const [dato, setDato] = useState<T | null>(null);
  return { dato, abierto: dato !== null, abrir: (d: T) => setDato(d), cerrar: () => setDato(null) };
}
